import React from 'react';
import { AlertTriangle, Shield, CheckCircle, AlertOctagon } from 'lucide-react';
import { motion } from 'framer-motion';

interface SurvivalCardProps {
    title: string;
    tips: string[];
    severity: 'critical' | 'high' | 'medium' | 'low';
    onClose?: () => void;
}

const SurvivalCard: React.FC<SurvivalCardProps> = ({ title, tips, severity, onClose }) => {
    const isCritical = severity === 'critical';
    const bg = isCritical ? 'bg-red-950/90' : 'bg-amber-950/90';
    const border = isCritical ? 'border-red-500' : 'border-amber-500';
    const text = isCritical ? 'text-red-100' : 'text-amber-100';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`w-full rounded-xl border ${border} ${bg} backdrop-blur-md overflow-hidden shadow-2xl mb-4`}
        >
            {/* Header */}
            <div className={`px-4 py-3 flex items-center justify-between border-b ${border} bg-black/20`}>
                <div className="flex items-center gap-2">
                    {isCritical ? <AlertOctagon className="text-red-500 animate-pulse" size={24} /> : <Shield className="text-amber-500" size={24} />}
                    <h3 className={`font-black uppercase tracking-wider ${isCritical ? 'text-red-500' : 'text-amber-500'}`}>
                        {isCritical ? 'CRITICAL SURVIVAL PROTOCOL' : 'SAFETY ADVISORY'}
                    </h3>
                </div>
                {onClose && (
                    <button onClick={onClose} className="text-white/50 hover:text-white transition">✕</button>
                )}
            </div>

            {/* Content */}
            <div className="p-4">
                <div className="mb-4">
                    <p className={`text-sm font-bold uppercase opacity-70 mb-1 ${text}`}>Incident</p>
                    <h4 className="text-xl font-bold text-white leading-tight">{title}</h4>
                </div>

                <div className="space-y-3">
                    <p className={`text-xs font-bold uppercase opacity-70 ${text}`}>Recommended Actions (AI Generated)</p>
                    {tips.map((tip, index) => (
                        <div key={index} className="flex items-start gap-3 bg-black/30 p-3 rounded-lg border border-white/5">
                            <div className={`mt-0.5 min-w-[20px] h-[20px] rounded-full flex items-center justify-center text-xs font-bold ${isCritical ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                {index + 1}
                            </div>
                            <p className="text-sm text-gray-200 leading-relaxed font-medium">
                                {tip}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className={`px-4 py-2 bg-black/40 text-[10px] uppercase font-mono text-center tracking-widest ${text} opacity-60`}>
                Generated by Digital Guardian AI • Follow Local Authorities
            </div>
        </motion.div>
    );
};

export default SurvivalCard;
